import React, { useMemo, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Prompt, WorkflowStep } from '../types';
import { ArrowUpIcon, ArrowDownIcon, TrashIcon, PlusIcon, KindGlyph, LinkIcon } from '../constants';
import { KIND_META, getKind, getSkillSlug } from '../utils/itemKinds';

interface WorkflowEditorProps {
  steps: WorkflowStep[];
  onChange: (steps: WorkflowStep[]) => void;
  /** Alle aktiven Einträge außer dem Workflow selbst, als mögliche Umsteigepunkte */
  candidates: Prompt[];
  onOpenItem: (id: string) => void;
}

const AutoTextarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => {
  const ref = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 520)}px`;
  }, [props.value]);
  return <textarea ref={ref} {...props} />;
};

/**
 * Workflow als Linienplan: jede Station ist ein Schritt, verknüpfte Prompts
 * und Skills erscheinen als Umsteigepunkte in ihrer eigenen Linienfarbe.
 */
const WorkflowEditor: React.FC<WorkflowEditorProps> = ({ steps, onChange, candidates, onOpenItem }) => {
  const byId = useMemo(() => new Map(candidates.map(c => [c.id, c])), [candidates]);
  const promptOptions = useMemo(() => candidates.filter(c => getKind(c) === 'prompt').sort((a, b) => a.name.localeCompare(b.name, 'de')), [candidates]);
  const skillOptions = useMemo(() => candidates.filter(c => getKind(c) === 'skill').sort((a, b) => a.name.localeCompare(b.name, 'de')), [candidates]);
  const lastAddedRef = useRef<string | null>(null);

  const patch = (id: string, p: Partial<WorkflowStep>) => onChange(steps.map(s => (s.id === id ? { ...s, ...p } : s)));
  const move = (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  const insertAt = (index: number) => {
    const step: WorkflowStep = { id: uuidv4(), title: '', content: '', refId: null };
    lastAddedRef.current = step.id;
    const next = [...steps];
    next.splice(index, 0, step);
    onChange(next);
  };

  useEffect(() => {
    if (!lastAddedRef.current) return;
    const el = document.getElementById(`step-title-${lastAddedRef.current}`);
    el?.focus();
    lastAddedRef.current = null;
  }, [steps.length]);

  return (
    <section aria-labelledby="workflow-steps-title">
      <div className="flex items-baseline justify-between mb-3">
        <h3 id="workflow-steps-title" className="label-xs">Strecke <span className="font-mono normal-case tracking-normal">· {steps.length} {steps.length === 1 ? 'Schritt' : 'Schritte'}</span></h3>
        <span className="text-xs text-graphite hidden sm:inline">Reihenfolge mit den Pfeilen ändern</span>
      </div>

      <ol className="route">
        {steps.map((step, i) => {
          const ref = step.refId ? byId.get(step.refId) : undefined;
          const refMissing = !!step.refId && !ref;
          const refKind = ref ? getKind(ref) : null;
          return (
            <li key={step.id} className="flex gap-4 pb-5 animate-station" style={{ animationDelay: `${Math.min(i, 8) * 35}ms` }}>
              <div className="route-station" aria-hidden="true">{i + 1}</div>
              <div className="flex-1 min-w-0 rounded-lg border border-rule bg-raised focus-within:border-kind-workflow/60 transition-colors">
                <div className="flex items-center gap-1 pl-3 pr-1 pt-1.5">
                  <label htmlFor={`step-title-${step.id}`} className="sr-only">Titel von Schritt {i + 1}</label>
                  <input
                    id={`step-title-${step.id}`}
                    type="text"
                    value={step.title}
                    onChange={(e) => patch(step.id, { title: e.target.value })}
                    placeholder={`Schritt ${i + 1}: Was passiert hier?`}
                    className="flex-1 min-w-0 bg-transparent border-0 shadow-none focus:ring-0 px-0 text-[15px] font-medium text-ink placeholder:text-graphite/70 py-1.5 focus:outline-none"
                  />
                  <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="icon-btn !h-8 !w-8 disabled:opacity-30" aria-label={`Schritt ${i + 1} nach oben`} title="Nach oben">
                    <ArrowUpIcon className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="icon-btn !h-8 !w-8 disabled:opacity-30" aria-label={`Schritt ${i + 1} nach unten`} title="Nach unten">
                    <ArrowDownIcon className="h-4 w-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onChange(steps.filter(s => s.id !== step.id))}
                    disabled={steps.length === 1}
                    className="icon-btn !h-8 !w-8 hover:!text-red-700 dark:hover:!text-red-400 disabled:opacity-30"
                    aria-label={`Schritt ${i + 1} entfernen`}
                    title="Schritt entfernen"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>

                <div className="px-3 pb-3">
                  <label htmlFor={`step-content-${step.id}`} className="sr-only">Anweisung für Schritt {i + 1}</label>
                  <AutoTextarea
                    id={`step-content-${step.id}`}
                    value={step.content}
                    onChange={(e) => patch(step.id, { content: e.target.value })}
                    rows={2}
                    placeholder="Anweisung, Prüfkriterium oder erwartetes Ergebnis dieses Schritts"
                    className="w-full bg-transparent border-0 shadow-none focus:ring-0 p-0 font-mono text-[13.5px] leading-[1.65] text-ink placeholder:text-graphite/70 resize-none focus:outline-none"
                  />

                  {/* Umsteigepunkt */}
                  <div className="flex flex-wrap items-center gap-2 mt-2 pt-2 border-t border-dashed border-rule">
                    {ref && refKind ? (
                      <>
                        <button
                          type="button"
                          onClick={() => onOpenItem(ref.id)}
                          className={`route-transfer ${KIND_META[refKind].text} hover:border-current`}
                          title={`${KIND_META[refKind].label} öffnen`}
                        >
                          <KindGlyph kind={refKind} className="h-4 w-4" />
                          <span className="text-ink">{refKind === 'skill' ? getSkillSlug(ref) : ref.name}</span>
                          <span className="kind-label">{KIND_META[refKind].label}</span>
                        </button>
                        <button type="button" onClick={() => patch(step.id, { refId: null })} className="text-xs text-graphite hover:text-ink underline-offset-2 hover:underline">
                          Verknüpfung lösen
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-xs text-graphite">{refMissing ? 'Verknüpfter Eintrag wurde gelöscht.' : 'Umsteigen auf'}</span>
                        <select
                          value=""
                          onChange={(e) => patch(step.id, { refId: e.target.value || null })}
                          className="h-8 rounded-md border border-rule bg-sheet text-xs text-ink pl-2 pr-7 py-0 focus:border-kind-workflow focus:ring-0 max-w-full"
                          aria-label={`Prompt oder Skill mit Schritt ${i + 1} verknüpfen`}
                        >
                          <option value="">Prompt oder Skill wählen …</option>
                          {skillOptions.length > 0 && (
                            <optgroup label="Skills">
                              {skillOptions.map(o => <option key={o.id} value={o.id}>{getSkillSlug(o)}</option>)}
                            </optgroup>
                          )}
                          {promptOptions.length > 0 && (
                            <optgroup label="Prompts">
                              {promptOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </optgroup>
                          )}
                        </select>
                        {refMissing && (
                          <button
                            type="button"
                            onClick={() => patch(step.id, { refId: null })}
                            className="text-xs text-graphite hover:text-ink underline-offset-2 hover:underline"
                          >
                            Verknüpfung entfernen
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </li>
          );
        })}

        <li className="flex items-center gap-4">
          <button
            type="button"
            onClick={() => insertAt(steps.length)}
            className="route-station !border-dashed !bg-sheet hover:!bg-kind-workflow/10"
            aria-label="Schritt am Ende anfügen"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
          <button type="button" onClick={() => insertAt(steps.length)} className="text-sm text-graphite hover:text-ink">
            Schritt anfügen
          </button>
        </li>
      </ol>

      {(promptOptions.length > 0 || skillOptions.length > 0) && (
        <p className="mt-4 flex items-center gap-2 text-xs text-graphite">
          <LinkIcon className="h-3.5 w-3.5 text-graphite" />
          Verknüpfte Skills werden beim Kopieren mit Namen genannt, verknüpfte Prompts mit vollem Text eingebettet.
        </p>
      )}
    </section>
  );
};

export default WorkflowEditor;
